import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'

import {
  BigCalendarFormDataIndividualSlots,
  BigCalendarModalIndividualFormProps,
} from '../../shared/calendar/utils/use-big-calendar-form'
import {
  HouseAvailabilityGlobalOrIndividualDto,
  HouseAvailabilityGlobalOrIndividualManyDto,
  HouseAvailabilityIndividualInputDto,
} from '../../../../../../lib/graphql'
import { convertTimezonedDateToIsoAtMidnight } from '../../../../../../utils/date-helpers'
import {
  divideByFactor,
  multiplyByFactor,
} from '../../../../../../utils/prices'

dayjs.extend(isSameOrBefore)

export const computeHouseAvailabilityIndividualToInputDto = (
  formData: BigCalendarModalIndividualFormProps,
): HouseAvailabilityGlobalOrIndividualDto => {
  const inputDto: HouseAvailabilityGlobalOrIndividualDto = {
    individual: computeOneAvailabilityIndividual(formData),
    global: null,
  }

  return inputDto
}

export const computeHouseAvailabilitiesIndividualToInputDto = (
  formData: BigCalendarFormDataIndividualSlots,
): HouseAvailabilityGlobalOrIndividualManyDto => {
  const { start: _start, end: _end } = formData.formValues

  const start = dayjs(_start)
  const end = dayjs(_end)

  const individual: HouseAvailabilityIndividualInputDto[] = []

  let current = start

  while (current.isSameOrBefore(end)) {
    individual.push({
      ...computeOneAvailabilityIndividual(formData),
      date: current.toDate(),
    })

    current = current.add(1, 'day')
  }
  return { individual, global: null }
}

export const computeHouseAvailabilityIndividualDefaultValues = (
  formData: BigCalendarModalIndividualFormProps,
): BigCalendarModalIndividualFormProps => {
  const { formValues } = formData

  const base = {
    corporateEvening: {
      isEnabled: formValues.corporateEvening.isEnabled,
    },
    house: formValues.house,
    houseId: formValues.houseId,
    nightlyTrip: {
      isEnabled: formValues.nightlyTrip.isEnabled,
      options: {
        sharedBedroomPrice: formValues.nightlyTrip.options?.sharedBedroomPrice
          ? divideByFactor(formValues.nightlyTrip.options.sharedBedroomPrice)
          : null,
        singleBedroomPrice: formValues.nightlyTrip.options?.singleBedroomPrice
          ? divideByFactor(formValues.nightlyTrip.options.singleBedroomPrice)
          : null,
      },
    },
    studyDay: {
      isEnabled: formValues.studyDay.isEnabled,
      options: {
        fullDayPrice: formValues.studyDay.options?.fullDayPrice
          ? divideByFactor(formValues.studyDay.options.fullDayPrice)
          : null,
        halfDayPrice: formValues.studyDay.options?.halfDayPrice
          ? divideByFactor(formValues.studyDay.options.halfDayPrice)
          : null,
        residentialPrice: formValues.studyDay.options?.residentialPrice
          ? divideByFactor(formValues.studyDay.options.residentialPrice)
          : null,
      },
    },
  }

  const computedFormData: BigCalendarModalIndividualFormProps =
    formData.type === 'cell'
      ? {
          type: formData.type,
          kind: formData.kind,
          formValues: {
            date: convertTimezonedDateToIsoAtMidnight(formData.formValues.date),
            ...base,
          },
        }
      : {
          type: formData.type,
          kind: formData.kind,
          formValues: {
            start: convertTimezonedDateToIsoAtMidnight(
              formData.formValues.start,
            ),
            end: convertTimezonedDateToIsoAtMidnight(formData.formValues.end),
            ...base,
          },
        }

  return computedFormData
}

const computeOneAvailabilityIndividual = (
  formData: BigCalendarModalIndividualFormProps,
): HouseAvailabilityIndividualInputDto => {
  const { formValues } = formData

  const inputDto: HouseAvailabilityIndividualInputDto = {
    date: convertTimezonedDateToIsoAtMidnight(
      formData.type === 'cell'
        ? formData.formValues.date
        : formData.formValues.start,
    ),
    corporateEvening: {
      isEnabled: formValues.corporateEvening.isEnabled,
    },
    houseId: formValues.houseId,
    nightlyTrip: {
      isEnabled: formValues.nightlyTrip.isEnabled,
      options: formValues.nightlyTrip.isEnabled
        ? {
            sharedBedroomPrice: formValues.nightlyTrip.options
              ?.sharedBedroomPrice
              ? multiplyByFactor(
                  formValues.nightlyTrip.options?.sharedBedroomPrice,
                )
              : null,
            singleBedroomPrice: formValues.nightlyTrip.options
              ?.singleBedroomPrice
              ? multiplyByFactor(
                  formValues.nightlyTrip.options?.singleBedroomPrice,
                )
              : null,
          }
        : null,
    },
    studyDay: {
      isEnabled: formValues.studyDay.isEnabled,
      options: formValues.studyDay.isEnabled
        ? {
            fullDayPrice: formValues.studyDay.options?.fullDayPrice
              ? multiplyByFactor(formValues.studyDay.options?.fullDayPrice)
              : null,
            halfDayPrice: formValues.studyDay.options?.halfDayPrice
              ? multiplyByFactor(formValues.studyDay.options?.halfDayPrice)
              : null,
            residentialPrice: formValues.studyDay.options?.residentialPrice
              ? multiplyByFactor(formValues.studyDay.options?.residentialPrice)
              : null,
          }
        : null,
    },
  }

  return inputDto
}
